//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'common/components/core/router';
import * as Immutable from 'immutable';
import { ReactTelInput } from 'common/components/core/react_tel_input';
import { translate } from 'react-i18next';

import { EnhancedField } from 'common/components/core/form/Field';
import { EntityModal } from 'common/entity/generic/EntityModal';
import { Select, SelectTypes } from 'common/components/core/select';
import { MultiTeamSelect } from 'common/entity/components/team';
import { HelpTooltip } from 'common/components/core/help';

import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { Contact, ContactType } from 'common/models/internal/Contact';
import { PhoneNumber } from 'common/models/internal/PhoneNumber';
import { TeamId } from 'common/models/internal/TeamId';
import { ApiError } from 'common/modules/api';

import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';

import ContactActions from 'common/redux/actions/contact';
import theme from './MiniContactMasterEditor.scss';

import type { Team } from 'common/models/internal/Team';
import type { List, Map } from 'immutable';
import I18nHelper from 'common/helpers/I18nHelper';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type Props = {
    theme: Object,
    t: (string, ?Object) => string,
    clientId: string,
    teams: Map<string, Team>,
    defaultSelectedTeams?: List<TeamId>,
    onDismiss: (?boolean) => void,
    addContact: ({ clientId: string, contactId: string }, Contact) => Promise<*>
};

type State = {
    contact: Contact,
    errorMessage?: ?string,
    isSaving?: boolean,
    isDuplicateId: boolean,
    isValidatingId: boolean
};

class MiniContactMasterEditor extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const { defaultSelectedTeams } = props;
        let defaultContact = Contact.fromJS({
            teams: [],
            type: ContactType.Customer
        });
        if (defaultSelectedTeams) {
            defaultContact = defaultContact.setTeams(defaultSelectedTeams);
        }
        this.state = {
            contact: defaultContact,
            defaultContact,
            isDuplicateId: false,
            isValidatingId: false
        };
    }

    onChange = (value: object) => {
        const { contact } = this.state;
        if ('code' in value) {
            this.setState({
                contact: contact.setCode(value.code)
            });
        }
        if ('id' in value) {
            this.setState({ contact: contact.setId(value.id) });
        }
    };

    onValidateDuplicateIdChange = ({ isDuplicateId, isValidatingId }) => {
        this.setState({ isDuplicateId, isValidatingId });
    };

    handleNameChange = (value: string) => {
        this.setState(state => {
            const { contact } = state;
            return {
                contact: contact.setName(value)
            };
        });
    };

    handlePhoneNumberChange = (value: string) => {
        this.setState(state => {
            const { contact } = state;
            let { phoneNumber } = contact;
            if (!phoneNumber) {
                phoneNumber = PhoneNumber.fromJS({
                    phoneNumber: value
                });
            } else {
                phoneNumber = phoneNumber.setPhoneNumber(value);
            }
            return {
                contact: contact.setPhoneNumber(phoneNumber)
            };
        });
    };

    handleContactTypeChange = (value: {
        value: $Values<typeof ContactType>,
        label: string
    }) => {
        this.setState(state => {
            const { contact } = state;
            return {
                contact: contact.setType(value.value)
            };
        });
    };

    clearErrorMessage = () => {
        // Clear message after 5 sec
        setTimeout(() => {
            this.setState({
                errorMessage: null
            });
        }, 5000);
    };

    handleContactAdd = () => {
        const { clientId, addContact, t, onDismiss } = this.props;
        const { contact, isDuplicateId } = this.state;
        const updatedContact = contact.setAllValidationErrors();
        this.setState({ contact: updatedContact });
        if (updatedContact.hasValidationErrors()) {
            this.setState(
                {
                    errorMessage: t(
                        'validations:genericFormValdiationFailMessage'
                    )
                },
                this.clearErrorMessage
            );
            return;
        }
        if (isDuplicateId) {
            this.setState(
                { errorMessage: t('changeIdError') },
                this.clearErrorMessage
            );
            return;
        }
        this.setState({ isSaving: true });
        addContact(
            {
                clientId,
                contactId: contact.id
            },
            contact
        )
            .then(res => onDismiss(res))
            .catch(error => {
                if (error instanceof ApiError) {
                    this.setState({
                        isSaving: false,
                        errorMessage: error.message
                    });
                    return;
                }
                throw error;
            });
    };

    onTeamSelect = selectedOptions => {
        const { clientId } = this.props;
        const { contact } = this.state;
        const teamIds = selectedOptions.map(option => {
            return TeamId.fromJS({
                clientId,
                teamId: option.value
            });
        });
        this.setState({
            contact: contact.setTeams(Immutable.List(teamIds))
        });
    };

    getContactTypes = () => {
        const ContactTypeOptions = [
            {
                value: ContactType.Customer,
                label: nsTranslate('miniContactMaster.contactType.customer')
            },
            {
                value: ContactType.Consignee,
                label: nsTranslate('miniContactMaster.contactType.consignee')
            },
            {
                value: ContactType.SalesRepresentative,
                label: nsTranslate(
                    'miniContactMaster.contactType.salesRepresentative'
                )
            },
            {
                value: ContactType.WarehouseManager,
                label: nsTranslate(
                    'miniContactMaster.contactType.warehouseManager'
                )
            },
            {
                value: ContactType.Distributor,
                label: nsTranslate('miniContactMaster.contactType.distributor')
            },
            {
                value: ContactType.Transporter,
                label: nsTranslate('miniContactMaster.contactType.transporter')
            },
            {
                value: ContactType.Supplier,
                label: nsTranslate('miniContactMaster.contactType.supplier')
            },
            {
                value: ContactType.Retailer,
                label: nsTranslate('miniContactMaster.contactType.retailer')
            }
        ];

        return ContactTypeOptions;
    };

    render() {
        const {
            className,
            theme,
            teams,
            onDismiss,
            t,
            defaultCountryISOCode,
            validateId,
            field,
            clientId
        } = this.props;
        const { contact, isSaving, errorMessage } = this.state;
        const translatedContactTypes = this.getContactTypes();

        return (
            <EntityModal
                theme={theme}
                title={t('miniContactMaster.addContact')}
                className={classnames(
                    className,
                    theme.contactMasterModalOverlay
                )}
                onDismiss={onDismiss}
                acceptErrorMessage={errorMessage}
                isLoading={isSaving}
                onAccept={this.handleContactAdd}
            >
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>
                        {t('miniContactMaster.code')}*
                    </span>
                    <CodeInputWithId
                        clientId={clientId}
                        disableDuplicateConfirmation
                        field={field}
                        validateDuplicateId={validateId}
                        onValidateDuplicateIdChange={
                            this.onValidateDuplicateIdChange
                        }
                        t={t}
                        theme={theme}
                        id={contact.id}
                        code={contact.code}
                        onChange={this.onChange}
                        placeholder={t('miniContactMaster.codePlaceholder')}
                        label={t('miniContactMaster.code')}
                        entity={contact}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>
                        {t('miniContactMaster.name')}*
                    </span>
                    <EnhancedField
                        type="text"
                        element="input"
                        value={contact.name}
                        placeholder={t('miniContactMaster.namePlaceholder')}
                        onChange={this.handleNameChange}
                        theme={theme}
                        error={contact.getValidationMessage('name')}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>
                        {t('miniContactMaster.type')}*
                    </span>
                    <Select
                        selectType={SelectTypes.Outline}
                        className={theme.typeSelector}
                        options={translatedContactTypes}
                        value={contact.type}
                        onChange={this.handleContactTypeChange}
                        isSearchable={false}
                        isClearable={false}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>
                        {t('miniContactMaster.phone')}*
                    </span>
                    <ReactTelInput
                        defaultCountryISOCode={defaultCountryISOCode}
                        onChange={this.handlePhoneNumberChange}
                        error={contact.getValidationMessage('phoneNumber')}
                    />
                </div>
                <div className={theme.fieldGroup}>
                    <span className={theme.label}>
                        <span>{t('miniContactMaster.teams')}</span>
                        <HelpTooltip
                            tooltipText={t('miniContactMaster.teamsHelp')}
                        />
                    </span>
                    <MultiTeamSelect
                        teams={teams}
                        onChange={this.onTeamSelect}
                        value={contact.teams}
                    />
                </div>
            </EntityModal>
        );
    }
}

const mapStateToProps = (state, props) => {
    const clientId = clientSelector(state, props);
    const teams = teamsSelector(state, props);
    const countryISOCode = ClientSelectors.getCountryISOCodeFromLocaleSettings(
        state,
        props
    );
    return {
        clientId,
        teams,
        defaultCountryISOCode: countryISOCode
    };
};

const actions = {
    addContact: ContactActions.addEntity,
    validateId: ContactActions.validateEntityIds
};

const ThemedMiniContactMasterEditor = themr(
    'MiniContactMasterEditor',
    theme
)(MiniContactMasterEditor);

export { MiniContactMasterEditor };
export default withRouter(
    connect(
        mapStateToProps,
        actions
    )(translate('entityManagement')(ThemedMiniContactMasterEditor))
);
